import { LoggedUser } from 'modules/users/models/LoggedUser'
import { UserDTO } from 'modules/users/models/User'

// PERMISOS DE USUARIOS
export const PERM_CREATE_PATIENT = 'users:create:patient'
export const PERM_CREATE_FAMILIAR = 'users:create:familiar'
export const PERM_CREATE_USER = 'users:create:any'
export const PERM_VIEW_ANY_USER = 'users:view:any'
export const PERM_VIEW_OWN_DATA = 'users:view:own'
export const PERM_EDIT_ANY_USER = 'users:edit:any'
export const PERM_EDIT_OWN_DATA = 'users:edit:own'
export const PERM_DELETE_USER = 'users:delete:any'
export const PERM_MANAGE_ROLES = 'users:manage:roles'
export const PERM_CHANGE_OWN_PASSWORD = 'users:change-password:own'

// PERMISOS DE HOME
export const PERM_VIEW_HOME = 'home:view'

// PERMISOS DE PACIENTES
export const PERM_VIEW_PATIENTS = 'patients:view:list'
export const PERM_VIEW_PATIENT_DETAILS = 'patients:view:details'
export const PERM_EDIT_PATIENT_DATA = 'patients:edit:data'
export const PERM_VIEW_PATIENT_STATS = 'patients:view:stats'
export const PERM_MANAGE_PATIENT_CIRCLE = 'patients:manage:circle'
export const PERM_VIEW_PATIENT_CALENDAR = 'patients:view:calendar'
export const PERM_EDIT_PATIENT_CALENDAR = 'patients:edit:calendar'

// PERMISOS DE FORMULARIOS
export const PERM_CREATE_FORMS = 'forms:create'
export const PERM_ANSWER_FORMS = 'forms:answer'
export const PERM_VIEW_FORMS = 'forms:view'
export const PERM_DELETE_FORMS = 'forms:delete'

// PERMISOS DE TRAINING BASE
export const PERM_CREATE_TRAINING = 'training:create'
export const PERM_VIEW_TRAINING = 'training:view'
export const PERM_EDIT_TRAINING = 'training:edit'
export const PERM_DELETE_TRAINING = 'training:delete'

// PERMISOS DE USER-TRAINING
export const PERM_CREATE_USER_TRAINING = 'user-training:create'
export const PERM_VIEW_USER_TRAINING = 'user-training:view'
export const PERM_EDIT_USER_TRAINING = 'user-training:edit'
export const PERM_DELETE_USER_TRAINING = 'user-training:delete'
export const PERM_COMPLETE_USER_TRAINING = 'user-training:complete'

// PERMISOS DE TRATAMIENTOS
export const PERM_CREATE_TREATMENT = 'treatments:create'
export const PERM_VIEW_TREATMENT = 'treatments:view'
export const PERM_EDIT_TREATMENT = 'treatments:edit'
export const PERM_DELETE_TREATMENT = 'treatments:delete'

// PERMISOS DE CALENDARIO
export const PERM_VIEW_CALENDAR = 'calendar:view:own'
export const PERM_EDIT_CALENDAR = 'calendar:edit:own'
export const PERM_VIEW_ANY_CALENDAR = 'calendar:view:any'
export const PERM_EDIT_ANY_CALENDAR = 'calendar:edit:any'

// PERMISOS DE CONTENIDO
export const PERM_CREATE_CONTENT = 'content:create'
export const PERM_EDIT_CONTENT = 'content:edit'
export const PERM_DELETE_CONTENT = 'content:delete'
export const PERM_VIEW_CONTENT = 'content:view'

// PERMISOS DE MENSAJERÍA
export const PERM_SEND_MESSAGES = 'messenger:send'
export const PERM_VIEW_MESSAGES = 'messenger:view'

// PERMISOS DE ESTADÍSTICAS
export const PERM_VIEW_OWN_STATS = 'stats:view:own'
export const PERM_VIEW_ANY_STATS = 'stats:view:any'

// PERMISOS DE ARCHIVOS
export const PERM_UPLOAD_FILES = 'files:upload'
export const PERM_VIEW_FILES = 'files:view'
export const PERM_DELETE_FILES = 'files:delete'
export const PERM_SHARE_FILES = 'files:share'

// PERMISOS DE WEARABLES
export const PERM_LINK_WEARABLE = 'wearables:link'
export const PERM_VIEW_WEARABLE = 'wearables:view'

// PERMISOS DE NOTIFICACIONES
export const PERM_SEND_NOTIFICATIONS = 'notifications:send'
export const PERM_RECEIVE_NOTIFICATIONS = 'notifications:receive'

// FUNCIONES BASE
export const hasPermission = (
  user: UserDTO | LoggedUser | undefined | null,
  permission: string
): boolean => {
  if (!user?.permissions) return false
  return user.permissions.includes(permission)
}

export const hasAnyPermission = (
  user: UserDTO | LoggedUser | undefined | null,
  permissions: string[]
): boolean => {
  if (!user?.permissions) return false
  return permissions.some((perm) => user.permissions?.includes(perm))
}

export const hasAllPermissions = (
  user: UserDTO | LoggedUser | undefined | null,
  permissions: string[]
): boolean => {
  if (!user?.permissions) return false
  return permissions.every((perm) => user.permissions?.includes(perm))
}

// PERMISOS DE USUARIO
export const canEditOwnData = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_OWN_DATA)
}

export const canChangeOwnPassword = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CHANGE_OWN_PASSWORD)
}

export const canCreateUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_USER)
}

export const canCreateFamiliar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_FAMILIAR)
}

export const canViewAnyUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_ANY_USER)
}

export const canEditAnyUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_ANY_USER)
}

export const canDeleteUser = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_USER)
}

export const canManageRoles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_MANAGE_ROLES)
}

// PERMISOS DE MENÚ
export const canAccessHome = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_HOME)
}

export const canViewPatients = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENTS)
}

export const canAccessCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [
    PERM_VIEW_CALENDAR,
    PERM_VIEW_ANY_CALENDAR,
    PERM_VIEW_PATIENT_CALENDAR,
  ])
}

export const canAccessCalendarMenu = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_CALENDAR)
}

// PERMISOS DE ESTADÍSTICAS
export const canViewStatistics = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_VIEW_OWN_STATS, PERM_VIEW_ANY_STATS])
}

export const canAccessStatisticsMenu = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_OWN_STATS)
}

export const canManageMedicines = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_CREATE_TREATMENT, PERM_EDIT_TREATMENT])
}

// PERMISOS DE TRAINING
export const canAccessTrainingCatalog = (
  user: UserDTO | LoggedUser | undefined | null
): boolean => {
  return hasPermission(user, PERM_VIEW_TRAINING)
}

export const canCreateTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_TRAINING)
}

export const canEditTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_TRAINING)
}

export const canDeleteTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_TRAINING)
}

// PERMISOS DE USER-TRAINING
export const canViewUserTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_USER_TRAINING)
}

export const canCreateUserTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_USER_TRAINING)
}

export const canEditUserTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_USER_TRAINING)
}

export const canDeleteUserTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_USER_TRAINING)
}

export const canCompleteUserTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_COMPLETE_USER_TRAINING)
}

export const canAccessTrainingMenu = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  if (hasPermission(user, PERM_CREATE_TRAINING)) return true
  if (hasPermission(user, PERM_COMPLETE_USER_TRAINING)) return true
  return false
}

export const canAccessTraining = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_VIEW_TRAINING, PERM_VIEW_USER_TRAINING])
}

// PERMISOS DE PACIENTES
export const canCreatePatient = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_PATIENT)
}

export const canEditPatientData = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_PATIENT_DATA)
}

export const canManagePatientCircle = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_MANAGE_PATIENT_CIRCLE)
}

export const canViewPatientDetails = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENT_DETAILS)
}

export const canViewPatientStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENT_STATS)
}

export const canViewPatientCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_PATIENT_CALENDAR)
}

export const canEditPatientCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_PATIENT_CALENDAR)
}

// PERMISOS DE TRATAMIENTOS
export const canCreateTreatment = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_TREATMENT)
}

export const canViewTreatment = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_TREATMENT)
}

export const canEditTreatment = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_TREATMENT)
}

export const canDeleteTreatment = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_TREATMENT)
}

// PERMISOS DE WEARABLES
export const canLinkWearable = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_LINK_WEARABLE)
}

export const canViewWearable = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_WEARABLE)
}

// PERMISOS DE FORMULARIOS
export const canAccessForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_ANSWER_FORMS, PERM_CREATE_FORMS, PERM_VIEW_FORMS])
}

export const canCreateForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_FORMS)
}

export const canAnswerForms = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_ANSWER_FORMS)
}

// PERMISOS DE ARCHIVOS
export const canUploadFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_UPLOAD_FILES)
}

export const canViewFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_FILES)
}

export const canDeleteFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_FILES)
}

export const canShareFiles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_SHARE_FILES)
}

export const canAccessResources = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_FILES)
}

// PERMISOS DE MENSAJERÍA
export const canSendMessages = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_SEND_MESSAGES)
}

export const canViewMessages = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_MESSAGES)
}

export const canAccessMessenger = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_SEND_MESSAGES, PERM_VIEW_MESSAGES])
}

// PERMISOS DE CONTENIDO
export const canCreateContent = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_CREATE_CONTENT)
}

export const canEditContent = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_CONTENT)
}

export const canDeleteContent = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_DELETE_CONTENT)
}

export const canViewContent = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_CONTENT)
}

export const canAccessNews = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_CONTENT)
}

export const canAccessArticles = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_CONTENT)
}

export const canManageContent = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasAnyPermission(user, [PERM_CREATE_CONTENT, PERM_EDIT_CONTENT])
}

// PERMISOS DE CALENDARIO
export const canViewCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_CALENDAR)
}

export const canEditCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_CALENDAR)
}

export const canViewAnyCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_ANY_CALENDAR)
}

export const canEditAnyCalendar = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_EDIT_ANY_CALENDAR)
}

// PERMISOS DE ESTADÍSTICAS
export const canViewOwnStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_OWN_STATS)
}

export const canViewAnyStats = (user: UserDTO | LoggedUser | undefined | null): boolean => {
  return hasPermission(user, PERM_VIEW_ANY_STATS)
}
